// src/components/LoginScreen.jsx
import React, { useEffect, useState } from "react";
import {
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
 import {
   doc, setDoc, serverTimestamp,
   collection, getDocs, query, where, limit
 } from "firebase/firestore";
import { db } from "../firebase";
import logo from "../assets/trapoundtop.png";
import { requireActiveSubscription as checkSubscription } from "../services/subscriptionGuard";
import { useLocation, useNavigate } from "react-router-dom";


// Domains
const API_BASE = "https://wixapi.onrender.com";
const MARKETING_BASE = "https://trapound.net";

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

export default function LoginScreen() {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [denied, setDenied] = useState(false);
  const [denyInfo, setDenyInfo] = useState({ reason: "", customerId: "", email: "" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showEmailLogin, setShowEmailLogin] = useState(false);

   const location = useLocation();
   const navigate = useNavigate();
   useEffect(() => {
     const st = location.state;
     if (st?.denied) {
       setDenied(true);
       setDenyInfo({
         reason: st.reason || "not active",
         customerId: st.customerId || "",
         email: st.email || ""
       });
       setErrorMsg(`Subscription: ${st.reason || "not active"}`);
       // clear the router state so refreshes don't re-trigger
       navigate(location.pathname, { replace: true });
     }
     // legacy fallback if something wrote to localStorage
     const le = localStorage.getItem("loginError");
     if (le) { setErrorMsg(le); localStorage.removeItem("loginError"); }
   }, [location, navigate]);


   useEffect(() => {
     const off = onAuthStateChanged(getAuth(), (u) => {

      // If you land on /login while logged out, ensure the
      // “No active subscription” panel is hidden.
      if (!u) {
        setDenied(false);
        setDenyInfo({ reason: "", customerId: "", email: "" });
        setErrorMsg("");
      }
     });
     return () => off();
   }, []);

  const OWNER_ALLOW = "0FLhCiGwLiR4BiC5uvpXd6tmLbg1";

  // Add this inside LoginScreen()
  async function handlePostLogin(user) {
    const userRef = doc(db, "users", user.uid);

    await setDoc(
      userRef,
      { email: user.email || "", lastLoginAt: serverTimestamp() },
      { merge: true }
    );

    const dest = "/dashboard";
    const result = await checkSubscription(user.uid, user.email);

    if (!result.ok) {
      const manualOk = await allowManualLoginByEmail(db, user.email);
      if (manualOk) {
        setErrorMsg("");
        navigate(dest, { replace: true });
        return;
      }
      
      setErrorMsg(`Your account doesn’t have access (${result.reason || "not active"}).`);
      setDenied(true);
      setDenyInfo({
        reason: result.reason || "not active",
        email: user.email || "",
        customerId: result.customerId || ""
      });
      return;
    }

    setErrorMsg("");
    navigate(dest, { replace: true });
  }




  function mapAuthError(err) {
    const code = err?.code || "";

    switch (code) {
      case "auth/invalid-credential":
        return "Incorrect email or password. Please try again.  "+
                  "If you normally sign in with Google, click “Sign in with Google”. ";

      case "auth/user-not-found":
        return     "We couldn’t confirm an email/password login for that email. " +
                  "If you normally sign in with Google, click “Sign in with Google”. ";

      case "auth/wrong-password":
        return "Incorrect password. Please try again.";

      case "auth/too-many-requests":
        return "Too many failed attempts. Please wait a few minutes and try again.";

      case "auth/user-disabled":
        return "This account has been disabled. Please contact support.";

      case "auth/popup-blocked":
        return "Popup was blocked by your browser. Please allow popups or try again.";

      case "auth/popup-closed-by-user":
        return "Sign-in popup was closed before completing login.";

      case "auth/network-request-failed":
        return "Network error. Please check your internet connection.";

      case "auth/email-already-in-use":
        return "An account already exists with this email. Try signing in instead.";

      case "auth/weak-password":
        return "Password is too weak. Please use at least 6 characters.";

      case "auth/invalid-email":
        return "Please enter a valid email address.";

      default:
        return "Login failed. Please try again.";
    }
  }


  async function allowManualLoginByEmail(db, email) {
    const em = String(email || "").trim().toLowerCase();
    if (!em) return false;
    const q = query(
      collection(db, "subscribers"),
      where("email", "==", em),
      where("ownerId", "==", OWNER_ALLOW),
      limit(1)
    );
    const snap = await getDocs(q);
    return snap.size > 0;
  }



async function handleGoogleLogin() {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    setErrorMsg("");
    setDenied(false);
    setLoading(true);

    try {
      // Use Popup universally. It's reliable and avoids the redirect state loss.
      const { user } = await signInWithPopup(auth, provider);
      
      // Because it's a popup, the page never unloads, so we can just await the post-login logic right here!
      await handlePostLogin(user);

    } catch (e) {
      console.error("[DEBUG] Google Login Error:", e);
      setErrorMsg(mapAuthError(e));
      setLoading(false);
    }
  }

  // async function handleGoogleLogin() {
  //   const auth = getAuth();
  //   const provider = new GoogleAuthProvider();
  //   // Force Google's account chooser every time
  //   provider.setCustomParameters({ prompt: 'select_account' });

  // await setPersistence(auth, browserSessionPersistence);

  //   setErrorMsg("");
  //   setDenied(false);
  //   setLoading(true);


  //   try {
  //     // Try popup first
  //     const { user } = await signInWithPopup(auth, provider);


  //     // Create/update /users/{uid} (rules must allow this)
  //     const userRef = doc(db, "users", user.uid);

  //     await setDoc(
  //       userRef,
  //       { email: user.email || "", lastLoginAt: serverTimestamp() },
  //       { merge: true }
  //     );

  //     // Destination (used for both normal and manual-allow logins)
  //    const dest = (location.state && location.state.from) || "/dashboard";

  //     // Check subscription (ACTIVE or TRIALING; live; endDate >= today)
  //     const result = await checkSubscription(user.uid, user.email);
  //     if (!result.ok) {
  //                     // 🔁 Manual-allow fallback: let in if there is a subscribers row
  //                     // for this email under the special ownerId.
  //                     const manualOk = await allowManualLoginByEmail(db, user.email);
  //                     if (manualOk) {
                       
  //                       setErrorMsg("");
  //                       navigate(dest, { replace: true });
  //                       return;
  //                     }
  //       setErrorMsg(`Your account doesn’t have access (${result.reason || "not active"}).`);
  //       // DO NOT navigate or signOut here.
  //       setDenied(true);
  //       setDenyInfo({
  //         reason: result.reason || "not active",
  //         email: user.email || "",
  //         customerId: result.customerId || ""
  //       });
  //       return; // stay on Login, show the error panel
  //     }

  //           // ✅ send authorized users to the dashboard (or to the original destination)
  //           setErrorMsg("");
  //           navigate(dest, { replace: true });
  //   } catch (e) {
     

  //     // Only fall back to redirect if popup was actually blocked
  //     if (e?.code === "auth/popup-blocked") {
  //       try {
 
  //         await signInWithRedirect(getAuth(), provider);
  //         return;
  //       } catch (e2) {
   
  //         setErrorMsg(e2.message || "Login failed");
  //       }
  //     } else {
  //       setErrorMsg(mapAuthError(e));
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // }

  async function handleEmailPasswordLogin(e) {
    e?.preventDefault?.();
    const auth = getAuth();

    setErrorMsg("");
    setDenied(false);
    setLoading(true);

    try {
      await setPersistence(auth, browserSessionPersistence);

      const em = String(email || "").trim();
      if (!em) throw new Error("Enter your email.");
      if (!password) throw new Error("Enter your password.");

      let userCred;
      try {
        userCred = await signInWithEmailAndPassword(auth, em, password);
      } catch (err) {
        // Optional: allow self-create if they don't exist yet
        if (err?.code === "auth/user-not-found") {
          userCred = await createUserWithEmailAndPassword(auth, em, password);
        } else {
          throw err;
        }
      }

      const user = userCred.user;

      // Create/update /users/{uid}
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        { email: user.email || em, lastLoginAt: serverTimestamp() },
        { merge: true }
      );

      const dest = "/dashboard";

      const result = await checkSubscription(user.uid, user.email || em);
      if (!result.ok) {
        const manualOk = await allowManualLoginByEmail(db, user.email || em);
        if (manualOk) {
          setErrorMsg("");
          navigate(dest, { replace: true });
          return;
        }

        setErrorMsg(`Your account doesn’t have access (${result.reason || "not active"}).`);
        setDenied(true);
        setDenyInfo({
          reason: result.reason || "not active",
          email: user.email || em,
          customerId: result.customerId || ""
        });
        return;
      }

      setErrorMsg("");
      navigate(dest, { replace: true });
    } catch (err) {
      
      setErrorMsg(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  }

async function handleForgotPassword() {
  const auth = getAuth();
  const em = String(email || "").trim().toLowerCase();
  if (!em) return setErrorMsg("Enter your email first, then click “Forgot password”.");

  try {
    const methods = await fetchSignInMethodsForEmail(auth, em);

    if (!methods.includes("password")) {
      return setErrorMsg(
        methods.includes("google.com")
          ? "This email uses Google sign-in. Click “Sign in with Google”."
          : "No password login is set for this email."
      );
    }

    await sendPasswordResetEmail(auth, em);
    setErrorMsg("Reset email sent. Check spam/promotions too.");
  } catch (err) {
    console.error("[LOGIN] reset error:", err);
    setErrorMsg(`Password reset failed (${err?.code || "unknown"}).`);
  }
}


  // Manage Billing (works even when denied)
  async function openPortalFromLogin(customerIdHint) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");
      const idToken = await user.getIdToken(true);

      // Secure portal (server derives customerId from email)
      const r = await fetch(`${API_BASE}/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.url) throw new Error(j?.error || `Portal failed (${r.status})`);
      window.location.assign(j.url);
    } catch (e) {
      console.error("[PORTAL] error:", e);
      setErrorMsg(e.message);

      // Fallback: if we already know a customerId (denyInfo), post simple form
      const cid = customerIdHint || denyInfo.customerId;
      if (cid) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${API_BASE}/create-portal-session`;

        const cust = document.createElement("input");
        cust.name = "customerId"; cust.value = cid; form.appendChild(cust);

        const ret = document.createElement("input");
        ret.name = "returnUrl"; ret.value = window.location.origin + "/account";
        form.appendChild(ret);

        document.body.appendChild(form);
        form.submit();
      }
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-blue-200 dark:from-gray-950 dark:to-blue-900">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl px-10 py-14 flex flex-col items-center border-2 border-blue-100 dark:border-blue-900 max-w-lg w-full">
        <div className="w-40 h-40 mb-5 rounded-full flex items-center justify-center shadow-2xl border-8 border-blue-600 dark:border-blue-400 bg-white dark:bg-gray-900 overflow-hidden">
          <img src={logo} alt="Trapound Logo" className="w-28 h-28 object-contain" />
        </div>

        <h1 className="text-3xl font-extrabold text-blue-900 dark:text-blue-100 mb-3 tracking-tight">
          Sign in to <span className="text-blue-700 dark:text-blue-400">Trapound</span>
        </h1>
        <p className="mb-7 text-gray-700 dark:text-gray-300 text-center">
          Your trading journal &amp; AI-powered analytics.
        </p>

        {/* Main sign-in button */}
        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-lg shadow transition disabled:opacity-60"
        >
          {/* Google "G" */}
          <svg viewBox="0 0 48 48" width={28} height={28} aria-hidden="true">
            <g>
              <path fill="#4285F4" d="M44.5 20H24v8.5h11.7C34.7 32.4 29.8 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8.1 3l6.3-6.3C34.3 5.1 29.4 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.6 0 19.7-7.7 20.7-18H44.5z" />
              <path fill="#34A853" d="M6.3 14.1l7 5.1C15.5 16.4 19.4 14 24 14c3.1 0 5.9 1.1 8.1 3l6.3-6.3C34.3 5.1 29.4 3 24 3 15.1 3 7.4 8.6 6.3 14.1z" />
              <path fill="#FBBC05" d="M24 45c5.4 0 10.3-1.9 14.1-5.1l-6.6-5.4C29.6 35.2 27 36 24 36c-5.7 0-10.6-3.6-12.4-8.5H6.3C7.4 39.4 15.1 45 24 45z" />
              <path fill="#EA4335" d="M44.5 20H24v8.5h11.7C37.6 32.3 29.8 36 24 36c-6.6 0-12-5.4-12-12 0-1.6.3-3.1.7-4.5h.1l7.1 5.1C20.5 27.6 22.1 28.5 24 28.5c2.5 0 4.5-1.3 5.6-3.2l8.4 6.4C37.6 38 31.2 45 24 45z" />
            </g>
          </svg>
          {loading ? "Checking subscription…" : "Sign in with Google"}
        </button>

        <div className="mt-6 w-full">
          <div className="flex items-center gap-3">
            <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
            <div className="text-sm text-gray-500 dark:text-gray-300 font-semibold">or</div>
            <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
          </div>

          {/* UPDATED: Softer "Slate" colors instead of harsh Black/White */}
            <button
              type="button"
              onClick={() => setShowEmailLogin((v) => !v)}
              className={`
                mt-5 w-full flex items-center justify-center gap-2 rounded-2xl h-12 font-bold text-lg transition-all duration-200
                ${
                  showEmailLogin
                    ? "bg-transparent text-gray-500 border border-gray-200 dark:border-gray-700 dark:text-gray-400" // Muted when open
                    : "bg-blue-800 text-white hover:bg-blue-700 shadow-md dark:bg-blue-700 dark:text-white dark:hover:bg-blue-600" // Prominent but soft
                }
              `}
            >
              {!showEmailLogin && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-5 h-5 text-gray-300"
                >
                  <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                  <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                </svg>
              )}
              {showEmailLogin ? "Hide email options" : "Sign in with Email"}
            </button>

            {showEmailLogin && (
              <form onSubmit={handleEmailPasswordLogin} className="mt-5 w-full space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="Email address"
                  className="tp-autofill w-full rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                />
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  placeholder="Password"
                  className="tp-autofill w-full rounded-xl border border-gray-300 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-2xl bg-blue-600 hover:bg-blue-700 text-white h-12 font-bold text-lg shadow-md transition disabled:opacity-60"
                >
                  {loading ? "Checking subscription…" : "Sign in"}
                </button>

                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full text-sm font-semibold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300 transition"
                >
                  Forgot password?
                </button>
              </form>
            )}
          </div>

        {errorMsg && (
          <div className="mt-4 text-red-600 font-semibold text-center max-w-sm">
            {errorMsg}
          </div>
        )}


        {/* Error label (for popup/redirect errors) */}
{/*        {errorMsg && (
          <div className="mt-4 text-red-600 font-semibold text-center max-w-sm">
            {errorMsg}
          </div>
        )}*/}

        {/* Denied panel (no active sub → show actions, do not refresh) */}
        {denied && (
          <div className="mt-6 w-full rounded-2xl border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/60 p-4 text-blue-800 dark:text-blue-200">
            <div className="font-bold mb-5 text-center">
              No active subscription for {denyInfo.email}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <a
                href="https://trapound.net"
                className="inline-flex items-center justify-center rounded-xl bg-gray-900 text-white dark:bg-blue-600 h-10 px-4 font-semibold"
              >
                Go to Website
              </a>
              <a
                href="mailto:support@trapound.net?subject=Access%20Help"
                className="inline-flex items-center justify-center rounded-xl border h-10 px-4 font-semibold text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Contact Support
              </a>
            </div>
          </div>
        )}

    </div>
    </div>
  );
}
